import { Link } from "react-router-dom";
import {
  ShieldCheck,
  PackageCheck,
  Wrench,
  Boxes,
  Headphones,
  Lock,
  ChevronRight,
  ArrowRight,
  CheckCircle2,
  Recycle,
  Layers,
  Cable,
  MonitorSmartphone,
  Tv,
  ShoppingBag,
} from "lucide-react";

const HERO_IMG =
  "https://static.prod-images.emergentagent.com/jobs/8f287472-b53b-4254-a417-af9039f0ca84/images/c429eb9ce2c29e47fbcb47a840365be6604d5bc96f147554a01328c80ffc5ddb.png";

const trustBadges = [
  { icon: ShieldCheck, label: "Serving Customers For Over 8 Years" },
  { icon: PackageCheck, label: "Open Box & Refurbished Specialists" },
  { icon: Wrench, label: "Quality Tested Products" },
  { icon: Headphones, label: "Customer Support Available" },
  { icon: Lock, label: "Secure Ordering Process" },
  { icon: Boxes, label: "Independently Owned Business" },
];

const categories = [
  {
    icon: PackageCheck,
    title: "Open Box Electronics",
    desc: "Products that have been opened but remain in excellent condition.",
    accent: "Inspected · Verified",
  },
  {
    icon: Wrench,
    title: "Refurbished Devices",
    desc: "Professionally inspected and tested products prepared for resale.",
    accent: "Restored · Tested",
  },
  {
    icon: Layers,
    title: "Customer Returns",
    desc: "Products sourced from return inventory and carefully evaluated.",
    accent: "Evaluated · Graded",
  },
  {
    icon: Boxes,
    title: "Overstock Inventory",
    desc: "Surplus inventory available at discounted pricing.",
    accent: "Surplus · Discounted",
  },
  {
    icon: MonitorSmartphone,
    title: "Media Devices",
    desc: "Various media and entertainment hardware products.",
    accent: "Hardware Only",
  },
  {
    icon: Cable,
    title: "Electronic Accessories",
    desc: "HDMI cables, remotes, adapters, power supplies, and related accessories.",
    accent: "Cables · Adapters",
  },
];

export default function Home() {
  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative overflow-hidden bg-white border-b border-gray-200">
        <div className="cnl-container py-16 md:py-24 lg:py-28 grid lg:grid-cols-12 gap-12 lg:gap-16 items-center">
          <div className="lg:col-span-7 cnl-fade-up">
            <div className="inline-flex items-center gap-2 rounded-sm border border-gray-200 bg-gray-50 px-3 py-1.5 mb-6">
              <span className="h-1.5 w-1.5 rounded-full bg-[#16A34A]" />
              <span className="text-[11px] font-semibold uppercase tracking-[0.18em] text-gray-700">
                Independent Reseller · Corona, CA · Est. 2020
              </span>
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.05] text-[#0B192C]">
              Quality Refurbished
              <br />
              Electronics{" "}
              <span className="relative inline-block">
                <span className="relative z-10">You Can Trust</span>
                <span className="absolute left-0 right-0 bottom-1 h-3 bg-[#2563EB]/15 z-0" />
              </span>
              .
            </h1>
            <p className="mt-6 text-[15px] sm:text-[17px] leading-relaxed text-gray-600 max-w-xl">
              At CNL Tech, we carefully source, inspect, and resell quality
              electronic products at competitive prices. Every item is reviewed
              and categorized to help customers make informed purchasing
              decisions.
            </p>
            <p className="mt-3 text-[13.5px] uppercase tracking-[0.18em] font-semibold text-[#1E3E62]">
              Open Box · Refurbished · Customer Returns · Overstock
            </p>

            <div className="mt-9 flex flex-col sm:flex-row gap-3 sm:gap-4">
              <Link
                to="/products"
                data-testid="hero-browse-products-btn"
                className="group inline-flex items-center justify-center gap-2 rounded-sm bg-[#0B192C] px-7 py-4 text-[13px] font-semibold uppercase tracking-[0.16em] text-white transition-all duration-200 hover:bg-[#1E3E62]"
              >
                Browse Products
                <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" />
              </Link>
              <Link
                to="/contact"
                data-testid="hero-contact-us-btn"
                className="inline-flex items-center justify-center gap-2 rounded-sm border border-gray-300 bg-white px-7 py-4 text-[13px] font-semibold uppercase tracking-[0.16em] text-[#0B192C] transition-colors hover:border-[#0B192C] hover:bg-gray-50"
              >
                Contact Us
              </Link>
            </div>

            {/* Mini stats */}
            <div className="mt-12 grid grid-cols-3 max-w-md gap-6 border-t border-gray-200 pt-8">
              <div>
                <div className="text-3xl font-bold tracking-tight text-[#0B192C]">8+</div>
                <div className="mt-1 text-[11px] uppercase tracking-[0.16em] text-gray-500 font-medium">
                  Years Serving
                </div>
              </div>
              <div>
                <div className="text-3xl font-bold tracking-tight text-[#0B192C]">4</div>
                <div className="mt-1 text-[11px] uppercase tracking-[0.16em] text-gray-500 font-medium">
                  Condition Grades
                </div>
              </div>
              <div>
                <div className="text-3xl font-bold tracking-tight text-[#0B192C]">100%</div>
                <div className="mt-1 text-[11px] uppercase tracking-[0.16em] text-gray-500 font-medium">
                  Independently Owned
                </div>
              </div>
            </div>
          </div>

          <div className="lg:col-span-5 cnl-fade-up cnl-delay-2">
            <div className="relative">
              <div className="absolute -inset-3 border border-gray-200 rounded-sm pointer-events-none" />
              <img
                src={HERO_IMG}
                alt="Technician inspecting electronic hardware in a refurbishment workshop"
                className="relative w-full h-[420px] lg:h-[520px] object-cover rounded-sm border border-gray-300"
                loading="eager"
              />
              <div className="absolute bottom-4 left-4 right-4 bg-white border border-gray-200 px-4 py-3 rounded-sm cnl-shadow-md">
                <div className="flex items-center gap-3">
                  <div className="flex h-9 w-9 items-center justify-center rounded-sm bg-[#0B192C] text-white">
                    <Wrench size={16} />
                  </div>
                  <div>
                    <div className="text-[12px] font-semibold uppercase tracking-[0.14em] text-[#1E3E62]">
                      Inspection Lab
                    </div>
                    <div className="text-[13px] text-gray-700">
                      Every applicable product is tested for functionality
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* TRUST STRIP */}
      <section className="bg-[#F9FAFB] border-b border-gray-200" data-testid="trust-section">
        <div className="cnl-container py-8 md:py-10">
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6">
            {trustBadges.map(({ icon: Icon, label }) => (
              <div
                key={label}
                className="flex items-start gap-3"
                data-testid={`trust-badge-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
              >
                <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-sm bg-white border border-gray-200 text-[#0B192C]">
                  <Icon size={16} strokeWidth={2.2} />
                </div>
                <span className="text-[12.5px] font-medium leading-snug text-gray-700">
                  {label}
                </span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ABOUT SECTION */}
      <section className="bg-white" data-testid="about-section">
        <div className="cnl-container py-20 lg:py-28 grid lg:grid-cols-12 gap-12 lg:gap-16 items-start">
          <div className="lg:col-span-5">
            <span className="text-[11px] font-semibold uppercase tracking-[0.22em] text-[#2563EB]">
              Who We Are
            </span>
            <h2 className="mt-4 text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-[#0B192C]">
              A small, independent reseller built around quality and trust.
            </h2>
          </div>
          <div className="lg:col-span-7 space-y-5 text-[15.5px] leading-relaxed text-gray-700">
            <p>
              CNL Tech is a small independently owned electronics reseller
              located in Corona, California. For more than 8 years, we have
              sourced, tested, and supplied customer-returned, open-box,
              refurbished, and surplus electronic products to customers seeking
              quality products at affordable prices.
            </p>
            <p>
              Our mission is to extend the life cycle of electronics while
              helping customers save money — without compromising on
              transparency or service.
            </p>
            <div className="pt-3 grid sm:grid-cols-2 gap-4">
              {[
                "Hardware-only product catalog",
                "Transparent condition grading",
                "Customer support during business hours",
                "Responsible reuse and refurbishment",
              ].map((item) => (
                <div key={item} className="flex items-start gap-2.5">
                  <CheckCircle2 size={18} className="text-[#16A34A] mt-0.5 shrink-0" />
                  <span className="text-[14.5px] text-gray-800">{item}</span>
                </div>
              ))}
            </div>
            <div className="pt-4">
              <Link
                to="/about"
                className="cnl-link text-[14px] font-semibold inline-flex items-center gap-1.5"
                data-testid="about-read-more-link"
              >
                Read more about CNL Tech <ChevronRight size={14} />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* PRODUCT CATEGORIES */}
      <section
        className="bg-[#F3F4F6] border-y border-gray-200"
        data-testid="categories-section"
      >
        <div className="cnl-container py-20 lg:py-28">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12">
            <div className="max-w-2xl">
              <span className="text-[11px] font-semibold uppercase tracking-[0.22em] text-[#2563EB]">
                Product Categories
              </span>
              <h2 className="mt-4 text-3xl sm:text-4xl font-bold tracking-tight text-[#0B192C]">
                Hardware sourced, tested, and clearly graded.
              </h2>
            </div>
            <Link
              to="/products"
              className="cnl-link text-[14px] font-semibold inline-flex items-center gap-1.5 self-start md:self-end"
              data-testid="categories-view-all-link"
            >
              View all categories <ChevronRight size={14} />
            </Link>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {categories.map((cat, idx) => (
              <div
                key={cat.title}
                data-testid={`category-card-${cat.title.toLowerCase().replace(/\s+/g, "-")}`}
                className={`group bg-white border border-gray-200 rounded-sm p-7 transition-all duration-200 hover:border-[#0B192C] hover:cnl-shadow-md`}
              >
                <div className="flex items-center justify-between mb-6">
                  <div className="flex h-11 w-11 items-center justify-center rounded-sm bg-[#0B192C] text-white transition-colors group-hover:bg-[#1E3E62]">
                    <cat.icon size={20} strokeWidth={2.1} />
                  </div>
                  <span className="text-[10.5px] font-semibold uppercase tracking-[0.18em] text-gray-500">
                    0{idx + 1}
                  </span>
                </div>
                <h3 className="text-[19px] font-semibold text-[#0B192C] tracking-tight">
                  {cat.title}
                </h3>
                <p className="mt-2.5 text-[14px] leading-relaxed text-gray-600">
                  {cat.desc}
                </p>
                <div className="mt-6 pt-4 border-t border-gray-100 flex items-center justify-between">
                  <span className="text-[11px] font-semibold uppercase tracking-[0.18em] text-gray-500">
                    {cat.accent}
                  </span>
                  <ArrowRight
                    size={16}
                    className="text-gray-400 transition-all group-hover:text-[#0B192C] group-hover:translate-x-1"
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PROCESS PREVIEW */}
      <section className="bg-[#0B192C] text-white relative cnl-grain" data-testid="process-preview">
        <div className="cnl-container py-20 lg:py-28 grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-5">
            <span className="text-[11px] font-semibold uppercase tracking-[0.22em] text-blue-300">
              Our Process
            </span>
            <h2 className="mt-4 text-3xl sm:text-4xl font-bold tracking-tight text-white">
              Five steps from sourcing to resale.
            </h2>
            <p className="mt-5 text-[15px] leading-relaxed text-gray-300 max-w-md">
              Every product moves through a consistent, transparent process so
              you know exactly what you’re buying.
            </p>
            <Link
              to="/process"
              data-testid="process-preview-cta"
              className="mt-8 inline-flex items-center gap-2 rounded-sm bg-white px-6 py-3.5 text-[13px] font-semibold uppercase tracking-[0.14em] text-[#0B192C] transition-colors hover:bg-gray-100"
            >
              See the full process <ArrowRight size={15} />
            </Link>
          </div>
          <div className="lg:col-span-7">
            <ol className="space-y-3">
              {[
                { n: "01", t: "Inventory Acquisition", d: "Customer returns, liquidation lots, overstock and surplus." },
                { n: "02", t: "Inspection", d: "Each product is visually inspected for condition." },
                { n: "03", t: "Testing", d: "Applicable products are tested for functionality." },
                { n: "04", t: "Condition Grading", d: "Categorized as New, Open Box, Refurbished, or Used." },
                { n: "05", t: "Resale", d: "Listed with transparent descriptions and customer support." },
              ].map((step) => (
                <li
                  key={step.n}
                  className="flex items-start gap-5 border border-[#1E3E62] rounded-sm px-5 py-4 hover:bg-[#1E3E62]/40 transition-colors"
                >
                  <span className="mono text-[24px] font-semibold text-blue-300 leading-none pt-1">
                    {step.n}
                  </span>
                  <div>
                    <h3 className="text-[17px] font-semibold text-white">{step.t}</h3>
                    <p className="mt-1 text-[14px] text-gray-300">{step.d}</p>
                  </div>
                </li>
              ))}
            </ol>
          </div>
        </div>
      </section>

      {/* SUSTAINABILITY TEASER */}
      <section className="bg-white border-b border-gray-200" data-testid="sustainability-teaser">
        <div className="cnl-container py-20 lg:py-24 grid md:grid-cols-2 gap-10 items-center">
          <div>
            <div className="inline-flex items-center justify-center h-12 w-12 rounded-sm bg-[#16A34A]/10 border border-[#16A34A]/30 text-[#16A34A]">
              <Recycle size={22} />
            </div>
            <h2 className="mt-5 text-3xl sm:text-4xl font-bold tracking-tight text-[#0B192C]">
              Giving electronics a second life.
            </h2>
            <p className="mt-4 text-[15px] leading-relaxed text-gray-600 max-w-lg">
              By supporting refurbishment and responsible resale, customers
              help reduce electronic waste and promote a more sustainable
              technology ecosystem.
            </p>
            <Link
              to="/sustainability"
              data-testid="sustainability-link"
              className="mt-7 inline-flex items-center gap-1.5 text-[14px] font-semibold text-[#0B192C] cnl-link"
            >
              Learn about our impact <ChevronRight size={14} />
            </Link>
          </div>
          <div className="grid grid-cols-2 gap-3">
            {[
              { t: "Reduced Waste", d: "Less e-waste in landfills" },
              { t: "Extended Life", d: "Hardware put back to use" },
              { t: "Affordable Tech", d: "Quality at lower cost" },
              { t: "Responsible Reuse", d: "Tested and graded resale" },
            ].map((b) => (
              <div
                key={b.t}
                className="border border-gray-200 rounded-sm p-5 hover:border-[#16A34A] transition-colors"
              >
                <div className="text-[15px] font-semibold text-[#0B192C]">
                  {b.t}
                </div>
                <div className="mt-1 text-[13px] text-gray-600">{b.d}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-[#F9FAFB]">
        <div className="cnl-container py-16 lg:py-20">
          <div className="border border-gray-200 bg-white rounded-sm p-8 md:p-12 flex flex-col md:flex-row md:items-center md:justify-between gap-6">
            <div className="max-w-xl">
              <h3 className="text-2xl sm:text-3xl font-bold tracking-tight text-[#0B192C]">
                Have a question about a product or condition grade?
              </h3>
              <p className="mt-2 text-[14.5px] text-gray-600">
                Our team is available Monday – Friday, 9:00 AM – 5:00 PM PT to
                help with compatibility, orders, and support.
              </p>
            </div>
            <Link
              to="/contact"
              data-testid="bottom-cta-contact"
              className="inline-flex items-center justify-center gap-2 rounded-sm bg-[#0B192C] px-7 py-4 text-[13px] font-semibold uppercase tracking-[0.16em] text-white transition-colors hover:bg-[#1E3E62] shrink-0"
            >
              Contact Support <ArrowRight size={15} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
